import type { ProspectResponse } from "../../../../types/api";
import {
  toggleFavorite,
  updatePitcherMetric,
  updatePositionPlayerMetric,
  updateProspectProfile,
  updateProspectTags,
  type UpdateProspectProfileData,
} from "../../../../services/prospects";
import { updateProspectTeam } from "../../../../services/teams";
import { ProspectViewerPanel } from "./ProspectViewerPanel";

interface ProspectQuickViewPanelProps {
  prospectId: string | null;
  isOpen: boolean;
  onClose: () => void;
  initialSection?: "profile" | "videos" | "notes" | "about" | null;
  /**
   * Fired after a change that a host list may be showing a stale copy of — a profile save
   * (name, position, grad year) or a delete. The host refetches; there is nothing to patch
   * locally because the host's rows are its own shape, not a ProspectResponse.
   */
  onProspectChanged?: () => void;
}

/**
 * The prospect viewer from the Players page, mounted anywhere.
 *
 * The panel itself is self-contained — it fetches the prospect and holds its own optimistic
 * copy — but every mutation is a callback so the Players page can reconcile the row in its
 * table. A host with no prospect table of its own (a roster board, say) has nothing to
 * reconcile, so these handlers are the bare service calls. Errors are deliberately left to
 * propagate: the panel reverts its optimistic state and surfaces the message itself, and
 * swallowing them here would leave a coach looking at an edit that never saved.
 */
export const ProspectQuickViewPanel = ({
  prospectId,
  isOpen,
  onClose,
  initialSection,
  onProspectChanged,
}: ProspectQuickViewPanelProps) => {
  const handleFavoriteToggle = async (
    id: string,
    currentIsFavorite: boolean
  ): Promise<void> => {
    await toggleFavorite(id, currentIsFavorite);
  };

  const handleTagUpdate = async (
    id: string,
    tagName: string,
    values: string[]
  ): Promise<void> => {
    await updateProspectTags(id, tagName, values);
    onProspectChanged?.();
  };

  const handlePositionPlayerMetricUpdate = async (
    id: string,
    field: string,
    value: number | null
  ): Promise<void> => {
    await updatePositionPlayerMetric(id, { [field]: value });
  };

  const handlePitcherMetricUpdate = async (
    id: string,
    field: string,
    value: number | null
  ): Promise<void> => {
    await updatePitcherMetric(id, { [field]: value });
  };

  const handleProfileSave = async (
    id: string,
    data: UpdateProspectProfileData
  ): Promise<ProspectResponse> => {
    const updated = await updateProspectProfile(id, data);
    onProspectChanged?.();
    return updated;
  };

  const handleJerseyNumberUpdate = async (
    teamId: string,
    id: string,
    jerseyNumber: number | null
  ): Promise<void> => {
    await updateProspectTeam(teamId, id, { jerseyNumber });
  };

  const handleProspectDeleted = () => {
    // The panel is showing a player who no longer exists, so it has to go regardless of
    // whether the host wants to refetch.
    onClose();
    onProspectChanged?.();
  };

  return (
    <ProspectViewerPanel
      prospectId={prospectId}
      isOpen={isOpen}
      onClose={onClose}
      initialSection={initialSection}
      onFavoriteToggle={handleFavoriteToggle}
      onTagUpdate={handleTagUpdate}
      onPositionPlayerMetricUpdate={handlePositionPlayerMetricUpdate}
      onPitcherMetricUpdate={handlePitcherMetricUpdate}
      onProfileSave={handleProfileSave}
      onJerseyNumberUpdate={handleJerseyNumberUpdate}
      onProspectDeleted={handleProspectDeleted}
    />
  );
};
